type WidgetRecordType = 'inventory' | 'floorplan';

type InventoryRecord = {
  id: string;
  floorplanName: string;
  manufacturer: string;
  type: WidgetRecordType;
  sections: number;
  beds: number;
  baths: number;
  sqft: number;
  status: string;
  price?: number;
  heroImage?: string;
  description?: string;
  tags?: string[];
  detailUrl?: string;
  width?: string;
  length?: string;
  pillText?: string;
  redBannerText?: string;
  featured?: boolean;
  location?: {
    community?: string;
    city?: string;
    state?: string;
    homesite?: string;
  };
};

type InventoryResponse = {
  data?: InventoryRecord[];
  meta?: {
    total?: number;
    count?: number;
  };
};

type SectionFilterValue = '1' | '2' | '3' | 'multi';
type HomeWidgetFilters = {
  sections?: Array<SectionFilterValue | number | string>;
  type?: WidgetRecordType[];
  manufacturer?: string[];
  sqftMax?: number;
};

type HomeWidgetOptions = {
  target: string | HTMLElement;
  brand?: string;
  title?: string;
  limit?: number;
  endpoint?: string;
  filters?: HomeWidgetFilters;
};

type Theme = {
  accent: string;
  accentSoft: string;
  badge: string;
  gradientFrom: string;
  gradientTo: string;
};

type NormalizedFilters = {
  sections: string[];
  type: WidgetRecordType[];
  manufacturer: string[];
  sqftMax?: number;
};

type ActiveFilterState = {
  sections: Set<string>;
  type: Set<WidgetRecordType>;
  manufacturer: Set<string>;
  sqftMax?: number;
};

type FilterButtonGroup = 'sections' | 'type';

type FilterButtonRef = {
  value: string;
  element: HTMLButtonElement;
  group: FilterButtonGroup;
};

type ResolvedOptions = {
  target: string | HTMLElement;
  brand: string;
  title: string;
  limit: number;
  endpoint: string;
  filters: NormalizedFilters;
  theme: Theme;
};

type WidgetFetchResult = {
  records: InventoryRecord[];
  total: number;
};

type PrimaryFilterState = {
  bedrooms: string;
  bathrooms: string;
  priceMax: number | undefined;
  sqftMin: number | undefined;
  sortBy: string;
  sections: string;
  search: string;
  pageStart: number;
};

type PrimaryControlRefs = {
  bedrooms: HTMLSelectElement;
  bathrooms: HTMLSelectElement;
  priceMax: HTMLInputElement;
  sqftMin: HTMLInputElement;
  sortBy: HTMLSelectElement;
  clear: HTMLButtonElement;
  sections: HTMLSelectElement;
  loadMore: HTMLButtonElement;
  search: HTMLInputElement;
  filterToggle: HTMLButtonElement;
};

const DEFAULT_ENDPOINT = '/.netlify/functions/inventory';
const DEFAULT_LIMIT = 8;
const DEFAULT_BRAND = 'Skyline Homes';
const DEFAULT_TITLE = 'Available Inventory';

const THEMES: Record<string, Theme> = {
  skyline: {
    accent: '#1d4ed8',
    accentSoft: 'rgba(29, 78, 216, 0.12)',
    badge: '#0f172a',
    gradientFrom: '#1d4ed8',
    gradientTo: '#1e40af'
  },
  clayton: {
    accent: '#0f7b6c',
    accentSoft: 'rgba(15, 123, 108, 0.12)',
    badge: '#134e4a',
    gradientFrom: '#0f766e',
    gradientTo: '#115e59'
  },
  default: {
    accent: '#475467',
    accentSoft: 'rgba(71, 84, 103, 0.12)',
    badge: '#1f2937',
    gradientFrom: '#4f46e5',
    gradientTo: '#4338ca'
  }
};

const SECTION_PILLS: Array<{ label: string; value: SectionFilterValue }> = [
  { label: 'Single Section', value: '1' },
  { label: 'Double Section', value: '2' },
  { label: 'Triple Section', value: '3' },
  { label: 'Multi Section', value: 'multi' }
];

const TYPE_PILLS: Array<{ label: string; value: WidgetRecordType }> = [
  { label: 'Move-in Ready', value: 'inventory' },
  { label: 'Floorplans', value: 'floorplan' }
];

const SORT_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Recommended', value: 'recommended' },
  { label: 'Price (Low to High)', value: 'price-asc' },
  { label: 'Price (High to Low)', value: 'price-desc' },
  { label: 'Sq Ft (Low to High)', value: 'sqft-asc' },
  { label: 'Sq Ft (High to Low)', value: 'sqft-desc' },
  { label: 'Bedrooms (Most to Fewest)', value: 'beds-desc' }
];

const PRICE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const BASE_STYLES = `
:host {
  all: initial;
}
*, *::before, *::after {
  box-sizing: border-box;
}
:host {
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0e1f3c;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
.widget-frame {
  background: #eef2f9;
  border-radius: 32px;
  padding: clamp(1.5rem, 4vw, 2.8rem);
  box-shadow: 0 40px 80px rgba(14, 31, 60, 0.12);
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}
.widget-header {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: flex-end;
}
.widget-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.75rem;
  color: #6c7487;
  margin: 0 0 0.25rem;
}
.widget-title {
  margin: 0;
  font-size: clamp(1.9rem, 4vw, 2.8rem);
}
.widget-subhead {
  margin: 0.75rem 0 0;
  max-width: 540px;
  color: #6c7487;
}
.widget-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.widget-status {
  font-weight: 600;
  color: #1d79ff;
}
.filters {
  background: #fff;
  border-radius: 32px;
  padding: clamp(1rem, 3vw, 1.75rem);
  box-shadow: 0 20px 50px rgba(14, 31, 60, 0.12);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.filters-primary,
.filters-extra {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  align-items: end;
}
.sort-inline {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.9rem;
}
.filters label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.9rem;
  color: #6c7487;
}
.filters label span {
  font-weight: 600;
}
.filters select,
.filters input {
  border-radius: 20px;
  border: 1px solid rgba(14, 31, 60, 0.12);
  padding: 0.65rem 0.9rem;
  background: #f5f7fb;
  font-size: 0.95rem;
}
.filters-extra {
  max-height: 0;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
}
.filters-extra.expanded {
  max-height: 600px;
  opacity: 1;
  pointer-events: auto;
  overflow: visible;
}
.filter-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}
.filter-actions .btn {
  flex: 1;
  min-width: 130px;
  text-align: center;
}
.pagination {
  display: flex;
  justify-content: center;
  padding-bottom: 2rem;
}
.pagination .btn {
  min-width: auto;
}
.btn {
  border-radius: 999px;
  border: none;
  padding: 0.8rem 1.4rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.btn.primary {
  background: linear-gradient(120deg, #1d79ff, #6dc2ff);
  color: #fff;
  box-shadow: 0 14px 40px rgba(29, 121, 255, 0.25);
}
.btn.secondary {
  background: #e9ecf5;
  color: #0e1f3c;
}
.btn.outline {
  background: transparent;
  border: 1px solid rgba(14, 31, 60, 0.15);
  color: #0e1f3c;
}
.btn:hover {
  transform: translateY(-1px);
}
.inventory-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
}
.home-card {
  background: #fff;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(14, 31, 60, 0.12);
  display: flex;
  flex-direction: column;
}
.img-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
}
.img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}
.home-card:hover .img-wrap img {
  transform: scale(1.05);
}
.status-pill {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding: 0.5rem 0.9rem;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
}
.status-pill span {
  display: block;
  font-size: 0.65rem;
  letter-spacing: 0.05em;
}
.ribbon {
  position: absolute;
  top: 1.2rem;
  right: -2.5rem;
  transform: rotate(35deg);
  background: #f0673d;
  color: #fff;
  padding: 0.45rem 3rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.featured-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: #1d79ff;
  color: #fff;
  padding: 0.5rem 0.9rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  z-index: 10;
}
.card-body {
  padding: 1.2rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.card-body .model {
  margin: 0;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-body .meta {
  margin: 0;
  color: #6c7487;
  line-height: 1.4;
  font-size: 0.82rem;
}
.card-body .price {
  margin: 0.4rem 0 1rem;
  font-size: 1rem;
  font-weight: 700;
  color: #000;
  text-align: center;
}
.price-label {
  color: #000;
  font-weight: 600;
  font-size: 1.5rem;
}
.price-value {
  color: #108a2f;
  font-weight: 700;
  font-size: 1.5rem;
}
.card-body .cta {
  margin-top: auto;
  width: 100%;
  text-align: center;
}
.state-block {
  grid-column: 1 / -1;
  padding: 2rem;
  border-radius: 24px;
  border: 1px dashed rgba(148, 163, 184, 0.7);
  text-align: center;
  color: #475467;
  background: rgba(255, 255, 255, 0.7);
}
.state-block.error {
  border-color: #f87171;
  color: #b91c1c;
}
.search-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
.search-wrap {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 0.9rem;
  pointer-events: none;
  color: #6c7487;
  display: flex;
  align-items: center;
}
.search-input {
  width: 100%;
  border-radius: 20px;
  border: 1px solid rgba(14, 31, 60, 0.12);
  padding: 0.65rem 0.9rem 0.65rem 2.5rem;
  background: #f5f7fb;
  font-size: 0.95rem;
  font-family: inherit;
  color: #0e1f3c;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.search-input:focus {
  outline: none;
  border-color: #1d79ff;
  box-shadow: 0 0 0 3px rgba(29, 121, 255, 0.1);
}
.search-input::placeholder {
  color: #a0a8bb;
}
.search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}
.filter-toggle-btn {
  display: none;
  white-space: nowrap;
  flex-shrink: 0;
}
.filter-toggle-btn.is-active {
  background: #0e1f3c;
  color: #fff;
}
.mobile-sort-btn {
  display: none;
}
.mobile-filter-row {
  display: none;
}
.mobile-filter-dropdown {
  display: none;
}
.mobile-sort-popup {
  display: none;
}
@media (max-width: 960px) {
  .inventory-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .widget-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
@media (max-width: 640px) {
  .widget-frame {
    gap: 0.6rem;
    padding: 1rem;
    border-radius: 20px;
  }
  .inventory-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .inventory-grid .home-card {
    width: calc(50% - 0.375rem);
    flex-shrink: 0;
  }
  .pagination {
    padding-bottom: 0.5rem;
  }
  .card-body {
    padding: 0.5rem 0.75rem 0.75rem;
    align-items: center;
    text-align: center;
    gap: 0.1rem;
  }
  .card-body .model {
    text-align: center;
    margin: 0;
  }
  .card-body .meta {
    text-align: center;
    margin: 0;
    line-height: 1.2;
  }
  .filter-toggle-btn {
    display: none;
  }
  .search-row {
    gap: 0.4rem;
  }
  .mobile-sort-btn {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .mobile-sort-btn svg {
    width: 14px;
    height: 14px;
  }
  .mobile-sort-popup {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.4rem;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 12px 40px rgba(14,31,60,0.18);
    padding: 0.5rem 0;
    z-index: 100;
    min-width: 180px;
  }
  .mobile-sort-popup.open {
    display: block;
  }
  .mobile-sort-popup button {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 0.55rem 1rem;
    font-size: 0.85rem;
    font-family: inherit;
    color: #0e1f3c;
    cursor: pointer;
  }
  .mobile-sort-popup button:hover,
  .mobile-sort-popup button.active {
    background: #f0f3fa;
    font-weight: 600;
  }
  .mobile-filter-row {
    display: flex;
    gap: 0.4rem;
    align-items: center;
    margin-top: 0.15rem;
  }
  .mobile-filter-row .mf-select {
    flex: 1;
    min-width: 0;
    border-radius: 20px;
    border: 1px solid rgba(14,31,60,0.12);
    padding: 0.5rem 0.75rem;
    background: #f5f7fb;
    font-size: 0.82rem;
    font-family: inherit;
    color: #0e1f3c;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236c7487' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.6rem center;
    padding-right: 1.8rem;
  }
  .mobile-filter-row .mf-all-btn {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
  }
  .mobile-filter-row .mf-all-btn.is-active {
    background: #0e1f3c;
    color: #fff;
  }
  .mobile-filter-dropdown {
    display: none;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 30px rgba(14,31,60,0.12);
    padding: 0.75rem;
    margin-top: 0.25rem;
  }
  .mobile-filter-dropdown.open {
    display: block;
  }
  .mobile-filter-dropdown label {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.85rem;
    color: #6c7487;
    margin-bottom: 0.5rem;
  }
  .mobile-filter-dropdown label span {
    font-weight: 600;
  }
  .mobile-filter-dropdown select,
  .mobile-filter-dropdown input {
    border-radius: 14px;
    border: 1px solid rgba(14,31,60,0.12);
    padding: 0.5rem 0.75rem;
    background: #f5f7fb;
    font-size: 0.85rem;
    font-family: inherit;
    width: 100%;
  }
  .filters {
    display: none;
  }
  .filters.filters-open {
    display: flex;
  }
  .filters-primary,
  .filters-extra {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  }
  .status-pill {
    padding: 0.3rem 0.5rem;
    font-size: 0.55rem;
    bottom: 0;
    top: auto;
    left: 0;
    right: 0;
    width: 100%;
    text-align: center;
    border-radius: 0;
    white-space: normal;
    line-height: 1.2;
  }
  .status-pill span {
    font-size: 0.5rem;
  }
  .ribbon {
    padding: 0.3rem 1.5rem;
    font-size: 0.55rem;
    top: 0.7rem;
    right: -1.8rem;
    transform: rotate(35deg);
  }
  .featured-badge {
    padding: 0.3rem 0.6rem;
    font-size: 0.65rem;
    top: 0.5rem;
    left: 0.5rem;
  }
  .price-label {
    font-size: 0.6rem;
    letter-spacing: 0.02em;
    line-height: 1;
    display: block;
    text-align: center;
    margin-bottom: 0.05rem;
  }
  .price-value {
    font-size: 1.1rem;
    letter-spacing: -0.01em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    display: block;
    text-align: center;
  }
  .card-body .price {
    white-space: nowrap;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: center;
    gap: 0;
    margin: 0.15rem 0 0.3rem;
    line-height: 1;
  }
  .meta-sqft-sep {
    display: none;
  }
  .meta-sqft {
    display: block;
  }
  .card-body .cta {
    font-size: 0.75rem;
    padding: 0.5rem 0.8rem;
    white-space: nowrap;
    width: 100%;
    text-align: center;
    margin-top: auto;
  }
}



`;

const isBrowser = () => typeof window !== 'undefined';

const resolveTarget = (target: string | HTMLElement): HTMLElement => {
  if (typeof target === 'string') {
    if (!isBrowser()) throw new Error('HomeWidget requires a browser environment.');
    const element = document.querySelector<HTMLElement>(target);
    if (!element) throw new Error(`HomeWidget target "${target}" not found.`);
    return element;
  }
  return target;
};

const normalizeSectionList = (input?: Array<string | number | SectionFilterValue>): string[] => {
  if (!input || !input.length) return [];
  const aliases: Record<string, SectionFilterValue> = {
    single: '1',
    singlewide: '1',
    double: '2',
    doublewide: '2',
    triple: '3',
    triplewide: '3',
    multi: 'multi',
    multisection: 'multi',
    mult: 'multi'
  };
  const result = new Set<string>();
  input.forEach((value) => {
    if (value == null) return;
    const raw = `${value}`.trim().toLowerCase();
    if (!raw) return;
    if (aliases[raw]) { result.add(aliases[raw]); return; }
    if (raw === 'multi-section') { result.add('multi'); return; }
    if (/^\d+$/.test(raw)) result.add(String(Number(raw)) as SectionFilterValue);
  });
  return Array.from(result.values());
};

const normalizeTypeList = (input?: WidgetRecordType[]): WidgetRecordType[] => {
  if (!input) return [];
  const allowed: WidgetRecordType[] = ['inventory', 'floorplan'];
  const result = new Set<WidgetRecordType>();
  input.forEach((value) => {
    if (!value) return;
    const normalized = value.toLowerCase() as WidgetRecordType;
    if (allowed.includes(normalized)) result.add(normalized);
  });
  return Array.from(result.values());
};

const normalizeManufacturerList = (input?: string[]): string[] => {
  if (!input) return [];
  const result = new Set<string>();
  input.forEach((value) => { if (value) result.add(value.toLowerCase().trim()); });
  return Array.from(result.values());
};

const mergeUnique = <T extends string>(a: T[], b: T[]): T[] =>
  Array.from(new Set<T>([...a, ...b]).values());

const parseUrlFilters = (): HomeWidgetFilters => {
  if (!isBrowser()) return {};
  const params = new URLSearchParams(window.location.search);
  const sanitize = (t: string) => params.get(t)?.split(',').map(r => r.trim()).filter(Boolean);
  return {
    sections: sanitize('sections'),
    type: sanitize('type')?.map(t => t.toLowerCase() as WidgetRecordType),
    manufacturer: sanitize('manufacturer')
  };
};

const readPathFilters = (): HomeWidgetFilters => {
  if (!isBrowser()) return {};
  const section = new URLSearchParams(window.location.search).get('section')?.toLowerCase().trim();
  console.log('[HomeWidget] readPathFilters - section param:', section);
  if (!section) return {};
  const filters: HomeWidgetFilters = {};
  switch (section) {
    case 'single': filters.sections = ['1']; break;
    case 'double': filters.sections = ['2']; break;
    case 'triple': filters.sections = ['3']; break;
    case 'tinyhomes': filters.sqftMax = 400; break;
  }
  console.log('[HomeWidget] readPathFilters - returning:', filters);
  return filters;
};

const resolveFilters = (config?: HomeWidgetFilters): NormalizedFilters => {
  const urlFilters = parseUrlFilters();
  const pathFilters = readPathFilters();
  const result = {
    sections: mergeUnique(
      normalizeSectionList(config?.sections),
      mergeUnique(normalizeSectionList(urlFilters.sections), normalizeSectionList(pathFilters.sections))
    ),
    type: mergeUnique(
      normalizeTypeList(config?.type),
      mergeUnique(normalizeTypeList(urlFilters.type), normalizeTypeList(pathFilters.type))
    ) as WidgetRecordType[],
    manufacturer: mergeUnique(
      normalizeManufacturerList(config?.manufacturer),
      mergeUnique(normalizeManufacturerList(urlFilters.manufacturer), normalizeManufacturerList(pathFilters.manufacturer))
    ),
    sqftMax: config?.sqftMax ?? pathFilters.sqftMax ?? urlFilters.sqftMax
  };
  console.log('[HomeWidget] resolveFilters - final resolved:', result);
  return result;
};

const resolveTheme = (brand?: string): Theme => {
  const key = brand?.toLowerCase().trim();
  if (key && THEMES[key]) return THEMES[key];
  return THEMES.skyline;
};

const resolveOptions = (options: HomeWidgetOptions): ResolvedOptions => {
  if (!options?.target) throw new Error('HomeWidget: target is required');
  const brand = options.brand ?? DEFAULT_BRAND;
  return {
    target: options.target,
    brand,
    title: options.title ?? DEFAULT_TITLE,
    limit: options.limit ?? DEFAULT_LIMIT,
    endpoint: options.endpoint ?? DEFAULT_ENDPOINT,
    filters: resolveFilters(options.filters),
    theme: resolveTheme(brand)
  };
};


class InventoryWidget {
  host: HTMLElement;
  shadow: ShadowRoot;
  grid!: HTMLElement;
  status!: HTMLElement;
  options: ResolvedOptions;
  filterButtons: Record<FilterButtonGroup, FilterButtonRef[]> = { sections: [], type: [] };
  state: ActiveFilterState;
  primaryControls!: PrimaryControlRefs;
  primaryFilters: PrimaryFilterState = {
    bedrooms: 'any',
    bathrooms: 'any',
    priceMax: undefined,
    sqftMin: undefined,
    sortBy: 'recommended',
    sections: 'all',
    search: '',
    pageStart: 0
  };
  allRecords: InventoryRecord[] = [];
  requestId = 0;
  destroyed = false;

  constructor(options: ResolvedOptions) {
    this.options = options;
    const mountTarget = resolveTarget(options.target);
    this.host = document.createElement('div');
    this.shadow = this.host.attachShadow({ mode: 'open' });
    this.state = {
      sections: new Set(options.filters.sections),
      type: new Set(options.filters.type),
      manufacturer: new Set(options.filters.manufacturer),
      sqftMax: options.filters.sqftMax
    };
    mountTarget.innerHTML = '';
    mountTarget.appendChild(this.host);
  }

  async mount() {
    this.renderShell();
    await this.refresh();
  }

  async refresh() {
    if (this.destroyed) return;
    const current = ++this.requestId;
    this.setStatus('Loading inventory\u2026');
    this.showState('loading', 'Fetching homes');
    try {
      const payload = await this.fetchInventory();
      if (this.destroyed || current !== this.requestId) return;
      this.renderRecords(payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load inventory';
      this.showState('error', message);
      this.setStatus('Unable to load homes');
    }
  }

  async updateFilters(filters?: HomeWidgetFilters) {
    if (filters) {
      const normalized = resolveFilters(filters);
      this.state.sections = new Set(normalized.sections);
      this.state.type = new Set(normalized.type);
      this.state.manufacturer = new Set(normalized.manufacturer);
      this.state.sqftMax = normalized.sqftMax;
      this.updateFilterButtons('sections');
      this.updateFilterButtons('type');
    }
    await this.refresh();
  }

  destroy() {
    this.destroyed = true;
    this.host.remove();
  }

  setStatus(message: string) {
    if (this.status) this.status.textContent = message;
  }

  renderShell() {
    const style = document.createElement('style');
    style.textContent = BASE_STYLES;

    const frame = document.createElement('div');
    frame.className = 'widget-frame';
    frame.innerHTML = `
      <div class="search-row">
        <div class="search-wrap">
          <span class="search-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input class="search-input" type="search" placeholder="Search floorplans\u2026" data-filter-search autocomplete="off" />
        </div>
        <button class="btn secondary filter-toggle-btn" type="button" data-filter-toggle>Filters</button>
        <div style="position:relative;flex-shrink:0">
          <button class="btn secondary mobile-sort-btn" type="button" data-mobile-sort-toggle>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M6 12h12M9 18h6"/></svg>
            Sort
          </button>
          <div class="mobile-sort-popup" data-mobile-sort-popup></div>
        </div>
      </div>
      <div class="mobile-filter-row" data-mobile-filter-row>
        <select class="mf-select" data-mf-price>
          <option value="">Max Price</option>
          <option value="100000">Under $100k</option>
          <option value="125000">Under $125k</option>
          <option value="150000">Under $150k</option>
          <option value="175000">Under $175k</option>
          <option value="200000">Under $200k</option>
          <option value="250000">Under $250k</option>
          <option value="300000">Under $300k</option>
        </select>
        <select class="mf-select" data-mf-sqft>
          <option value="">Min Sq Ft</option>
          <option value="500">500+ Sq Ft</option>
          <option value="800">800+ Sq Ft</option>
          <option value="1000">1,000+ Sq Ft</option>
          <option value="1200">1,200+ Sq Ft</option>
          <option value="1400">1,400+ Sq Ft</option>
          <option value="1600">1,600+ Sq Ft</option>
          <option value="2000">2,000+ Sq Ft</option>
        </select>
        <button class="btn secondary mf-all-btn" type="button" data-mf-all-toggle>All Filters</button>
      </div>
      <div class="mobile-filter-dropdown" data-mobile-filter-dropdown></div>
      <section class="filters" aria-label="Inventory filters">
        <div class="filters-primary">
          <label>
            <span>Bedrooms</span>
            <select data-filter-bedrooms disabled>
              <option value="any">Any</option>
            </select>
          </label>
          <label>
            <span>Bathrooms</span>
            <select data-filter-bathrooms disabled>
              <option value="any">Any</option>
            </select>
          </label>
          <label>
            <span>Price (max)</span>
            <input data-filter-price type="number" min="0" inputmode="numeric" placeholder="No max" disabled />
          </label>
          <label>
            <span>Sq Ft (min)</span>
            <input data-filter-sqft type="number" min="0" inputmode="numeric" placeholder="Any" disabled />
          </label>
          <label>
            <span>Sections</span>
            <select data-filter-sections disabled>
              <option value="all">Any</option>
            </select>
          </label>
          <label class="sort-inline">
            <span>Sort By</span>
            <select data-filter-sort disabled></select>
          </label>
          <div class="filter-actions">
            <button class="btn secondary" type="button" data-filter-clear disabled>Clear</button>
          </div>
        </div>
      </section>
      <section class="inventory-grid" aria-live="polite"></section>
    `;

    const hiddenFilters = document.createElement('div');
    hiddenFilters.style.display = 'none';
    hiddenFilters.append(
      this.createFilterGroup('Sections', SECTION_PILLS, 'sections'),
      this.createFilterGroup('Type', TYPE_PILLS, 'type')
    );
    frame.appendChild(hiddenFilters);

    this.grid = frame.querySelector('.inventory-grid') as HTMLElement;

    const loadMoreWrap = document.createElement('div');
    loadMoreWrap.className = 'pagination';
    const loadMoreBtn = document.createElement('button');
    loadMoreBtn.className = 'btn outline';
    loadMoreBtn.type = 'button';
    loadMoreBtn.disabled = true;
    loadMoreBtn.setAttribute('aria-label', 'Load more homes');
    loadMoreBtn.setAttribute('data-load-more', '');
    loadMoreBtn.textContent = 'Load more';
    loadMoreWrap.appendChild(loadMoreBtn);
    this.grid.insertAdjacentElement('afterend', loadMoreWrap);

    this.primaryControls = {
      bedrooms: frame.querySelector('[data-filter-bedrooms]') as HTMLSelectElement,
      bathrooms: frame.querySelector('[data-filter-bathrooms]') as HTMLSelectElement,
      priceMax: frame.querySelector('[data-filter-price]') as HTMLInputElement,
      sqftMin: frame.querySelector('[data-filter-sqft]') as HTMLInputElement,
      sortBy: frame.querySelector('[data-filter-sort]') as HTMLSelectElement,
      clear: frame.querySelector('[data-filter-clear]') as HTMLButtonElement,
      sections: frame.querySelector('[data-filter-sections]') as HTMLSelectElement,
      loadMore: loadMoreBtn,
      search: frame.querySelector('[data-filter-search]') as HTMLInputElement,
      filterToggle: frame.querySelector('[data-filter-toggle]') as HTMLButtonElement
    };

    this.populateSortOptions();
    this.wirePrimaryFilters();
    this.wireLoadMore();
    this.populateSectionsOptions();
    this.disablePrimaryControls(true);
    this.wireMobileFilters(frame);

    this.shadow.innerHTML = '';
    this.shadow.append(style, frame);
  }

  populateSortOptions() {
    const select = this.primaryControls.sortBy;
    select.innerHTML = '';
    SORT_OPTIONS.forEach((opt) => {
      const el = document.createElement('option');
      el.value = opt.value;
      el.textContent = opt.label;
      select.appendChild(el);
    });
    select.value = this.primaryFilters.sortBy;
  }

  wirePrimaryFilters() {
    const { bedrooms, bathrooms, priceMax, sqftMin, sortBy, clear, sections, search } = this.primaryControls;
    bedrooms.addEventListener('change', () => {
      this.primaryFilters.bedrooms = bedrooms.value;
      this.handlePrimaryFiltersChange();
    });
    bathrooms.addEventListener('change', () => {
      this.primaryFilters.bathrooms = bathrooms.value;
      this.handlePrimaryFiltersChange();
    });
    priceMax.addEventListener('input', () => {
      this.primaryFilters.priceMax = this.readNumericInput(priceMax);
      this.handlePrimaryFiltersChange();
    });
    sqftMin.addEventListener('input', () => {
      this.primaryFilters.sqftMin = this.readNumericInput(sqftMin);
      this.handlePrimaryFiltersChange();
    });
    sortBy.addEventListener('change', () => {
      this.primaryFilters.sortBy = sortBy.value;
      this.handlePrimaryFiltersChange();
    });
    sections.addEventListener('change', () => {
      this.primaryFilters.sections = sections.value;
      this.handlePrimaryFiltersChange();
    });
    clear.addEventListener('click', () => this.handleClearPrimaryFilters());
    search.addEventListener('input', () => {
      this.primaryFilters.search = search.value;
      this.handlePrimaryFiltersChange();
    });
    this.primaryControls.filterToggle.addEventListener('click', () => {
      const filtersEl = this.shadow.querySelector('.filters') as HTMLElement;
      if (filtersEl) {
        const isOpen = filtersEl.classList.toggle('filters-open');
        this.primaryControls.filterToggle.classList.toggle('is-active', isOpen);
      }
    });
  }

  wireLoadMore() {
    this.primaryControls.loadMore.addEventListener('click', () => {
      this.primaryFilters.pageStart += this.options.limit;
      this.renderNextPage();
    });
  }

  wireMobileFilters(frame: HTMLElement) {
    // --- Mobile Sort button & popup ---
    const sortToggle = frame.querySelector('[data-mobile-sort-toggle]') as HTMLButtonElement;
    const sortPopup = frame.querySelector('[data-mobile-sort-popup]') as HTMLElement;
    if (sortToggle && sortPopup) {
      SORT_OPTIONS.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = opt.label;
        if (opt.value === this.primaryFilters.sortBy) btn.classList.add('active');
        btn.addEventListener('click', () => {
          this.primaryFilters.sortBy = opt.value;
          this.primaryControls.sortBy.value = opt.value;
          sortPopup.querySelectorAll('button').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          sortPopup.classList.remove('open');
          this.handlePrimaryFiltersChange();
        });
        sortPopup.appendChild(btn);
      });
      sortToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        sortPopup.classList.toggle('open');
      });
      this.shadow.addEventListener('click', () => sortPopup.classList.remove('open'));
    }

    // --- Inline Price / SqFt selects ---
    const mfPrice = frame.querySelector('[data-mf-price]') as HTMLSelectElement;
    const mfSqft = frame.querySelector('[data-mf-sqft]') as HTMLSelectElement;
    if (mfPrice) {
      if (this.primaryFilters.priceMax) mfPrice.value = String(this.primaryFilters.priceMax);
      mfPrice.addEventListener('change', () => {
        const val = mfPrice.value ? Number(mfPrice.value) : undefined;
        this.primaryFilters.priceMax = val;
        this.primaryControls.priceMax.value = mfPrice.value;
        this.handlePrimaryFiltersChange();
      });
    }
    if (mfSqft) {
      if (this.primaryFilters.sqftMin) mfSqft.value = String(this.primaryFilters.sqftMin);
      mfSqft.addEventListener('change', () => {
        const val = mfSqft.value ? Number(mfSqft.value) : undefined;
        this.primaryFilters.sqftMin = val;
        this.primaryControls.sqftMin.value = mfSqft.value;
        this.handlePrimaryFiltersChange();
      });
    }

    // --- All Filters toggle ---
    const allBtn = frame.querySelector('[data-mf-all-toggle]') as HTMLButtonElement;
    const filterDropdown = frame.querySelector('[data-mobile-filter-dropdown]') as HTMLElement;
    if (!allBtn || !filterDropdown) return;

    allBtn.addEventListener('click', () => {
      const isOpen = filterDropdown.classList.toggle('open');
      allBtn.classList.toggle('is-active', isOpen);
      if (!isOpen) return;
      filterDropdown.innerHTML = '';

      // Bedrooms
      const bedLabel = document.createElement('label');
      bedLabel.innerHTML = '<span>Bedrooms</span>';
      const bedSelect = document.createElement('select');
      bedSelect.innerHTML = '<option value="any">Any</option>';
      [1,2,3,4,5].forEach(n => {
        const o = document.createElement('option');
        o.value = String(n);
        o.textContent = `${n}+`;
        bedSelect.appendChild(o);
      });
      bedSelect.value = this.primaryFilters.bedrooms;
      bedSelect.addEventListener('change', () => {
        this.primaryFilters.bedrooms = bedSelect.value;
        this.primaryControls.bedrooms.value = bedSelect.value;
        this.handlePrimaryFiltersChange();
      });
      bedLabel.appendChild(bedSelect);
      filterDropdown.appendChild(bedLabel);

      // Bathrooms
      const bathLabel = document.createElement('label');
      bathLabel.innerHTML = '<span>Bathrooms</span>';
      const bathSelect = document.createElement('select');
      bathSelect.innerHTML = '<option value="any">Any</option>';
      [1,2,3,4].forEach(n => {
        const o = document.createElement('option');
        o.value = String(n);
        o.textContent = `${n}+`;
        bathSelect.appendChild(o);
      });
      bathSelect.value = this.primaryFilters.bathrooms;
      bathSelect.addEventListener('change', () => {
        this.primaryFilters.bathrooms = bathSelect.value;
        this.primaryControls.bathrooms.value = bathSelect.value;
        this.handlePrimaryFiltersChange();
      });
      bathLabel.appendChild(bathSelect);
      filterDropdown.appendChild(bathLabel);

      // Sections
      const secLabel = document.createElement('label');
      secLabel.innerHTML = '<span>Sections</span>';
      const secSelect = document.createElement('select');
      secSelect.innerHTML = '<option value="all">Any</option>';
      [{ l: 'Single', v: '1' }, { l: 'Double', v: '2' }, { l: 'Triple', v: '3' }].forEach(s => {
        const o = document.createElement('option');
        o.value = s.v;
        o.textContent = s.l;
        secSelect.appendChild(o);
      });
      secSelect.value = this.primaryFilters.sections;
      secSelect.addEventListener('change', () => {
        this.primaryFilters.sections = secSelect.value;
        this.primaryControls.sections.value = secSelect.value;
        this.handlePrimaryFiltersChange();
      });
      secLabel.appendChild(secSelect);
      filterDropdown.appendChild(secLabel);
    });
  }

  handlePrimaryFiltersChange() {
    this.updateClearButtonState();
    this.primaryFilters.pageStart = 0;
    this.refreshVisibleRecords();
  }

  handleClearPrimaryFilters() {
    this.primaryFilters = {
      bedrooms: 'any',
      bathrooms: 'any',
      priceMax: undefined,
      sqftMin: undefined,
      sortBy: 'recommended',
      sections: 'all',
      search: '',
      pageStart: 0
    };
    const { bedrooms, bathrooms, priceMax, sqftMin, sortBy, sections, search } = this.primaryControls;
    bedrooms.value = 'any';
    bathrooms.value = 'any';
    priceMax.value = '';
    sqftMin.value = '';
    sortBy.value = 'recommended';
    sections.value = 'all';
    search.value = '';
    this.handlePrimaryFiltersChange();
  }

  disablePrimaryControls(disabled: boolean) {
    const { bedrooms, bathrooms, priceMax, sqftMin, sortBy, clear, sections, loadMore, search } = this.primaryControls;
    [bedrooms, bathrooms, priceMax, sqftMin, sortBy, sections, search].forEach((el) => { el.disabled = disabled; });
    clear.disabled = disabled || !this.hasActivePrimaryFilters();
    loadMore.disabled = disabled;
  }

  hasActivePrimaryFilters(): boolean {
    return !(
      this.primaryFilters.bedrooms === 'any' &&
      this.primaryFilters.bathrooms === 'any' &&
      !this.primaryFilters.priceMax &&
      !this.primaryFilters.sqftMin &&
      this.primaryFilters.sortBy === 'recommended' &&
      this.primaryFilters.sections === 'all' &&
      !this.primaryFilters.search
    );
  }

  updateClearButtonState() {
    this.primaryControls.clear.disabled = !this.hasActivePrimaryFilters();
  }

  readNumericInput(input: HTMLInputElement): number | undefined {
    if (!input.value) return undefined;
    const n = Number(input.value);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }

  updatePrimaryFilterOptions(records: InventoryRecord[]) {
    const beds = this.collectUniqueNumbers(records, 'beds');
    const baths = this.collectUniqueNumbers(records, 'baths');
    this.populateNumericSelect(this.primaryControls.bedrooms, beds, 'bedrooms');
    this.populateNumericSelect(this.primaryControls.bathrooms, baths, 'bathrooms');
    this.populateSectionsOptions();
  }

  populateSectionsOptions() {
    const select = this.primaryControls.sections;
    const sectionValues = new Set<string>();
    this.allRecords.forEach((r) => { if (r.sections) sectionValues.add(String(r.sections)); });
    const prev = select.value;
    select.innerHTML = '';
    const anyOpt = document.createElement('option');
    anyOpt.value = 'all';
    anyOpt.textContent = 'Any';
    select.appendChild(anyOpt);
    Array.from(sectionValues).sort((a, b) => Number(a) - Number(b)).forEach((v) => {
      const opt = document.createElement('option');
      opt.value = v;
      opt.textContent = `${v} Section${v !== '1' ? 's' : ''}`;
      select.appendChild(opt);
    });
    if (this.state.sections.size > 0) {
      const first = Array.from(this.state.sections)[0];
      if (first && Array.from(sectionValues).includes(first)) {
        select.value = first;
        return;
      }
    }
    if (Array.from(sectionValues).includes(prev)) {
      select.value = prev;
    } else {
      select.value = 'all';
    }
  }

  collectUniqueNumbers(records: InventoryRecord[], key: keyof InventoryRecord): number[] {
    const set = new Set<number>();
    records.forEach((r) => {
      const val = r[key];
      if (typeof val === 'number' && Number.isFinite(val)) set.add(val);
    });
    return Array.from(set.values()).sort((a, b) => a - b);
  }

  populateNumericSelect(select: HTMLSelectElement, values: number[], key: 'bedrooms' | 'bathrooms') {
    const prev = select.value;
    select.innerHTML = '';
    const anyOpt = document.createElement('option');
    anyOpt.value = 'any';
    anyOpt.textContent = 'Any';
    select.appendChild(anyOpt);
    values.forEach((v) => {
      const opt = document.createElement('option');
      opt.value = String(v);
      opt.textContent = `${v}+`;
      select.appendChild(opt);
    });
    const hasValues = values.length > 0;
    select.disabled = !hasValues;
    if (hasValues && values.some((v) => String(v) === prev)) {
      select.value = prev;
      if (key === 'bedrooms') this.primaryFilters.bedrooms = prev;
      else this.primaryFilters.bathrooms = prev;
    } else {
      select.value = 'any';
      if (key === 'bedrooms') this.primaryFilters.bedrooms = 'any';
      else this.primaryFilters.bathrooms = 'any';
    }
  }

  refreshVisibleRecords() {
    if (!this.grid) return;
    if (!this.allRecords.length) {
      this.showState('empty', 'No homes match your filters yet.');
      this.setStatus('0 homes available');
      return;
    }
    this.primaryFilters.pageStart = 0;
    this.renderFromRecords(this.allRecords);
  }

  renderFromRecords(records: InventoryRecord[]) {
    if (!this.applyPrimaryFilters(records).length) {
      this.showState('empty', 'No homes match your filters yet.');
      this.setStatus('0 homes available');
      this.updateLoadMoreButton(0, 0);
      return;
    }
    this.grid.innerHTML = '';
    this.renderNextPage();
  }

  renderNextPage() {
    if (!this.allRecords.length) return;
    const filtered = this.applyPrimaryFilters(this.allRecords);
    if (!filtered.length) return;
    const start = this.primaryFilters.pageStart;
    const end = start + this.options.limit;
    filtered.slice(start, end).forEach((record) => {
      this.grid.appendChild(this.createCard(record));
    });
    this.updateLoadMoreButton(end, filtered.length);
    this.setStatus(`Showing ${Math.min(end, filtered.length)} of ${filtered.length} homes`);
  }

  updateLoadMoreButton(shown: number, total: number) {
    this.primaryControls.loadMore.style.display = shown < total ? 'inline-flex' : 'none';
  }

  applyPrimaryFilters(records: InventoryRecord[]): InventoryRecord[] {
    const filtered = records.filter((r) => {
      if (this.primaryFilters.bedrooms !== 'any' && r.beds < Number(this.primaryFilters.bedrooms)) return false;
      if (this.primaryFilters.bathrooms !== 'any' && r.baths < Number(this.primaryFilters.bathrooms)) return false;
      if (typeof this.primaryFilters.priceMax === 'number' && (typeof r.price !== 'number' || r.price > this.primaryFilters.priceMax)) return false;
      if (typeof this.primaryFilters.sqftMin === 'number' && r.sqft < this.primaryFilters.sqftMin) return false;
      if (typeof this.state.sqftMax === 'number' && r.sqft > this.state.sqftMax) return false;
      if (this.primaryFilters.sections !== 'all' && String(r.sections) !== this.primaryFilters.sections) return false;
      if (this.primaryFilters.search) {
        const q = this.primaryFilters.search.toLowerCase();
        if (!r.floorplanName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
    return this.sortRecords(filtered);
  }

  sortRecords(records: InventoryRecord[]): InventoryRecord[] {
    const sorted = [...records];
    switch (this.primaryFilters.sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
        break;
      case 'sqft-asc':
        sorted.sort((a, b) => a.sqft - b.sqft);
        break;
      case 'sqft-desc':
        sorted.sort((a, b) => b.sqft - a.sqft);
        break;
      case 'beds-desc':
        sorted.sort((a, b) => b.beds - a.beds);
        break;
      default:
        // Recommended: featured first
        sorted.sort((a, b) =>
          a.featured && !b.featured ? -1 : !a.featured && b.featured ? 1 : 0
        );
        break;
    }
    return sorted;
  }

  createFilterGroup<T extends string>(label: string, pills: Array<{ label: string; value: T }>, group: FilterButtonGroup) {
    const container = document.createElement('div');
    container.className = 'hw__filter-group';
    const text = document.createElement('span');
    text.className = 'hw__filter-label';
    text.textContent = label;
    container.appendChild(text);
    pills.forEach((pill) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hw__filter-pill';
      button.textContent = pill.label;
      button.dataset.value = pill.value;
      button.addEventListener('click', () => this.handleFilterToggle(group, pill.value));
      this.filterButtons[group].push({ value: pill.value, element: button, group });
      container.appendChild(button);
    });
    this.updateFilterButtons(group);
    return container;
  }

  handleFilterToggle(group: FilterButtonGroup, rawValue: string) {
    if (group === 'sections') {
      if (this.state.sections.has(rawValue)) this.state.sections.delete(rawValue);
      else this.state.sections.add(rawValue);
    } else {
      const typed = rawValue as WidgetRecordType;
      if (this.state.type.has(typed)) this.state.type.delete(typed);
      else this.state.type.add(typed);
    }
    this.updateFilterButtons(group);
    void this.refresh();
  }

  updateFilterButtons(group: FilterButtonGroup) {
    if (group === 'sections') {
      this.filterButtons.sections.forEach((b) => {
        b.element.classList.toggle('is-active', this.state.sections.has(b.value));
      });
      return;
    }
    this.filterButtons.type.forEach((b) => {
      b.element.classList.toggle('is-active', this.state.type.has(b.value as WidgetRecordType));
    });
  }

  showState(state: 'loading' | 'empty' | 'error', message: string) {
    this.grid.innerHTML = '';
    const node = document.createElement('div');
    node.className = `state-block${state === 'error' ? ' error' : ''}`;
    node.textContent = message;
    this.grid.appendChild(node);
  }

  async fetchInventory(): Promise<WidgetFetchResult> {
    const url = new URL(this.options.endpoint, window.location.origin);
    if (this.state.sections.size) url.searchParams.set('sections', Array.from(this.state.sections.values()).join(','));
    if (this.state.type.size) url.searchParams.set('type', Array.from(this.state.type.values()).join(','));
    if (this.state.manufacturer.size) url.searchParams.set('manufacturer', Array.from(this.state.manufacturer.values()).join(','));
    console.log('[HomeWidget] fetchInventory - state:', {
      sections: this.state.sections,
      type: this.state.type,
      manufacturer: this.state.manufacturer,
      sqftMax: this.state.sqftMax
    });
    console.log('[HomeWidget] fetchInventory - URL:', url.toString());

    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Inventory request failed (${response.status})`);

    const payload = (await response.json()) as InventoryResponse;
    const records = payload.data ?? [];
    const total = payload.meta?.count ?? payload.meta?.total ?? records.length;
    console.log('[HomeWidget] fetchInventory - received', records.length, 'records');
    return { records, total };
  }

  renderRecords(payload: WidgetFetchResult) {
    this.allRecords = payload.records;
    if (!payload.records.length) {
      this.disablePrimaryControls(true);
      this.showState('empty', 'No homes match your filters yet.');
      this.setStatus('0 homes available');
      return;
    }
    this.disablePrimaryControls(false);
    this.updatePrimaryFilterOptions(payload.records);
    this.updateClearButtonState();
    this.renderFromRecords(payload.records);
  }

  createCard(record: InventoryRecord) {
    const card = document.createElement('article');
    card.className = 'home-card';

    const media = document.createElement('div');
    media.className = 'img-wrap';

    if (record.heroImage) {
      const img = document.createElement('img');
      img.src = record.heroImage;
      img.alt = `${record.floorplanName} hero image`;
      media.appendChild(img);
    }

    if (record.pillText) {
      const pill = document.createElement('span');
      pill.className = 'status-pill';
      pill.textContent = record.pillText;
      media.appendChild(pill);
    }

    if (record.redBannerText) {
      const ribbon = document.createElement('span');
      ribbon.className = 'ribbon';
      ribbon.textContent = record.redBannerText;
      media.appendChild(ribbon);
    }

    if (record.featured) {
      const badge = document.createElement('span');
      badge.className = 'featured-badge';
      badge.textContent = 'Featured';
      media.appendChild(badge);
    }

    const body = document.createElement('div');
    body.className = 'card-body';

    const title = document.createElement('h3');
    title.className = 'model';
    title.textContent = record.floorplanName;
    body.appendChild(title);

    const metaBedBath = document.createElement('p');
    metaBedBath.className = 'meta';
    metaBedBath.innerHTML = `${record.beds} Bed \u2022 ${record.baths} Bath<span class="meta-sqft-sep"> \u2022 </span><span class="meta-sqft">${record.sqft.toLocaleString()} Sq Ft</span>`;
    body.appendChild(metaBedBath);

    if (record.location?.community || record.location?.city) {
      const location = document.createElement('p');
      location.className = 'meta';
      const segments = [record.location.community, record.location.city, record.location.state].filter(Boolean);
      location.textContent = segments.join(', ');
      body.appendChild(location);
    }

    const price = document.createElement('p');
    price.className = 'price';
    price.innerHTML = typeof record.price === 'number'
      ? `<span class="price-label">Base Price</span> <span class="price-value">${PRICE.format(record.price)}</span>`
      : '<span>Call for pricing</span>';
    body.appendChild(price);

    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'btn primary cta';
    cta.textContent = 'Learn More';
    cta.addEventListener('click', (event) => {
      event.stopPropagation();
      this.navigateToFloorplan(record);
    });
    body.appendChild(cta);

    card.append(media, body);
    card.addEventListener('click', () => this.navigateToFloorplan(record));
    return card;
  }

  navigateToFloorplan(record: InventoryRecord) {
    const slug = record.floorplanName.toLowerCase().replace(/\s+/g, '-');
    const url = `${window.location.origin}/floorplan?floorplan=${slug}`;
    window.location.href = url;
  }
}

export type HomeWidgetController = {
  refresh: () => Promise<void>;
  updateFilters: (filters?: HomeWidgetFilters) => Promise<void>;
  destroy: () => void;
};

const HomeWidget = {
  async init(options: HomeWidgetOptions): Promise<HomeWidgetController> {
    const resolved = resolveOptions(options);
    const widget = new InventoryWidget(resolved);
    await widget.mount();
    return {
      refresh: () => widget.refresh(),
      updateFilters: (filters?: HomeWidgetFilters) => widget.updateFilters(filters),
      destroy: () => widget.destroy()
    };
  }
};

declare global {
  interface Window {
    HomeWidget?: typeof HomeWidget;
  }
}

if (isBrowser()) {
  window.HomeWidget = HomeWidget;
}

export { HomeWidget };
export type { HomeWidgetFilters, HomeWidgetOptions };
export default HomeWidget;
